// Decision logic for bulk (CSV) onboarding runs — plan PR B, B2–B7.
//
// Pure functions and one sequential batch runner, kept out of the components
// so the rules that decide what gets dispatched are testable on their own. The
// SERVER enforces ordering, exclusivity and the credit ceiling inside each step
// claim; these decide what the console offers and how it reacts, so being
// wrong here wastes a click or stops a batch — it cannot double-apply anything.

import {
  ONBOARDING_STEPS,
  type OnboardingCsvError,
  type OnboardingCsvInvalid,
  type OnboardingCsvRow,
  type OnboardingPreflight,
  type OnboardingRun,
  type OnboardingStepName,
  type OnboardingStepStatus,
  type OnboardingTarget,
} from '../../../api/onboardingTypes'
import { ApiRequestError } from '../../../api/client'
import { isApiRecord, type ApiValue } from '../../../api/responseDecoders'

export const CSV_TEMPLATE =
  'email,first_name,credits,org_slug,org_display_name\n' +
  'partner@example.com,Ana,100000,acme-studio,Acme Studio\n'

/** The browser-side half of the encoding rule: refuse anything that is not UTF-8. */
export class CsvEncodingError extends Error {
  constructor() {
    super('NOT_UTF8')
    this.name = 'CsvEncodingError'
  }
}

/**
 * Read a CSV file as strict UTF-8 (B1). Excel's "Unicode Text" is UTF-16 and
 * some locales default to legacy code pages; decoding those leniently would
 * turn Korean names into mojibake that then gets mailed to partners.
 */
export async function readCsvFile(file: Blob): Promise<string> {
  const bytes = await file.arrayBuffer()
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    throw new CsvEncodingError()
  }
}

function numberOrNull(value: ApiValue): number | null {
  return typeof value === 'number' ? value : null
}

function stringOrNull(value: ApiValue): string | null {
  return typeof value === 'string' ? value : null
}

function numberArrayOrNull(value: ApiValue): number[] | null {
  if (!Array.isArray(value)) return null
  const numbers = value.filter((item): item is number => typeof item === 'number')
  return numbers.length === value.length ? numbers : null
}

const CONFLICT_KINDS: ReadonlyArray<NonNullable<OnboardingCsvError['conflict_kind']>> = [
  'in_flight',
  'already_onboarded',
  'pending_elsewhere',
]

function conflictKind(value: ApiValue): OnboardingCsvError['conflict_kind'] {
  return CONFLICT_KINDS.find((kind) => kind === value) ?? null
}

function parseCsvError(value: ApiValue): OnboardingCsvError | null {
  if (!isApiRecord(value) || typeof value.message !== 'string') return null
  return {
    record: numberOrNull(value.record),
    lines: numberArrayOrNull(value.lines),
    column: stringOrNull(value.column),
    message: value.message,
    conflict_kind: conflictKind(value.conflict_kind),
  }
}

function parseCsvRow(value: ApiValue): OnboardingCsvRow | null {
  if (!isApiRecord(value)) return null
  const lines = numberArrayOrNull(value.lines)
  if (
    typeof value.record !== 'number' ||
    typeof value.email !== 'string' ||
    typeof value.credits !== 'number' ||
    lines === null
  ) {
    return null
  }
  return {
    record: value.record,
    lines,
    email: value.email,
    first_name: stringOrNull(value.first_name),
    credits: value.credits,
    org_slug: stringOrNull(value.org_slug),
    org_display_name: stringOrNull(value.org_display_name),
  }
}

/**
 * The 422 CSV_INVALID body from `ApiRequestError.errorData`, or null if the
 * error is anything else. Fails closed: an entry that does not parse is dropped
 * only if it cannot be read at all, and an unreadable body is "not CSV_INVALID",
 * so the generic error banner shows it instead.
 */
export function parseOnboardingCsvInvalid(errorData: ApiValue | null): OnboardingCsvInvalid | null {
  if (!isApiRecord(errorData) || !isApiRecord(errorData.detail)) return null
  const detail = errorData.detail
  if (detail.code !== 'CSV_INVALID' || !Array.isArray(detail.errors)) return null
  const errors = detail.errors.map(parseCsvError)
  if (errors.some((error) => error === null)) return null
  return {
    errors: errors.filter((error): error is OnboardingCsvError => error !== null),
    rows: Array.isArray(detail.rows)
      ? detail.rows
          .map(parseCsvRow)
          .filter((row): row is OnboardingCsvRow => row !== null)
      : [],
    total_credits: numberOrNull(detail.total_credits),
    credit_ceiling: numberOrNull(detail.credit_ceiling),
  }
}

export function stepOf(target: OnboardingTarget, step: OnboardingStepName) {
  return target.steps.find((s) => s.step === step)
}

export function stepStatus(
  target: OnboardingTarget,
  step: OnboardingStepName,
): OnboardingStepStatus {
  return stepOf(target, step)?.status ?? 'pending'
}

// Every earlier step, not just the previous one — the server's rule. A team
// step created already skipped must not make credits look ready before provision.
function predecessorDone(target: OnboardingTarget, step: OnboardingStepName): boolean {
  const index = ONBOARDING_STEPS.indexOf(step)
  return ONBOARDING_STEPS.slice(0, Math.max(index, 0)).every((earlier) => {
    const status = stepStatus(target, earlier)
    return status === 'succeeded' || status === 'skipped'
  })
}

/**
 * Targets a preflight says this environment cannot complete. A run-wide
 * trigger failure blocks every target (B5).
 */
export function blockedTargetIds(preflight: OnboardingPreflight | undefined): Set<string> {
  if (!preflight) return new Set()
  if (!preflight.trigger_enforcing) {
    return new Set(preflight.targets.map((target) => target.target_id))
  }
  return new Set(
    preflight.targets
      .filter((target) => target.blocking_reason !== null)
      .map((target) => target.target_id),
  )
}

/**
 * Ready for step S (B4): predecessor settled, S `pending` — NOT `failed`, which
 * is retried only by an explicit per-row Retry — not blocked, run not cancelled.
 * In row order, which is the order targets come back in.
 */
export function readyTargets(
  run: OnboardingRun,
  step: OnboardingStepName,
  blocked: Set<string>,
): OnboardingTarget[] {
  if (run.status === 'cancelled' || run.status === 'completed' || run.status === 'failed') return []
  return run.targets.filter(
    (target) =>
      target.email !== null &&
      !blocked.has(target.target_id) &&
      stepStatus(target, step) === 'pending' &&
      predecessorDone(target, step) &&
      (step !== 'team' || target.team_dependency_ready !== false),
  )
}

/** Team setup may include pending members unlocked by a selected owner in this same batch.
 * This is a plan, not readiness: dispatch re-reads each team target and still uses readyTargets.
 */
export function batchTargets(run: OnboardingRun, step: OnboardingStepName, blocked: Set<string>): OnboardingTarget[] {
  const ready = readyTargets(run, step, blocked)
  if (step !== 'team' || run.status !== 'running' && run.status !== 'pending') return ready
  const readyIds = new Set(ready.map((target) => target.target_id))
  const planned = run.targets.filter((target) => readyIds.has(target.target_id) || (
    target.team_mode === 'group_member' && target.team_dependency_ready === false &&
    target.email !== null && !blocked.has(target.target_id) &&
    stepStatus(target, 'team') === 'pending' && predecessorDone(target, 'team') &&
    run.targets.some((owner) => owner.target_id === target.team_owner_target_id &&
      owner.team_mode === 'create' && readyIds.has(owner.target_id))
  ))
  // An owner always precedes its dependent members, even if a scoped response is reordered.
  return [...planned.filter((target) => target.team_mode !== 'group_member'),
    ...planned.filter((target) => target.team_mode === 'group_member')]
}

export function failedTargets(run: OnboardingRun, step: OnboardingStepName): OnboardingTarget[] {
  return run.targets.filter((target) => stepStatus(target, step) === 'failed')
}

export type StepBucket = OnboardingStepStatus | 'blocked'

/**
 * Per-step counts that sum to the number of targets (B7). A pending step of a
 * blocked target counts as `blocked` INSTEAD of `pending`; claimed and running
 * share a bucket, as on the server.
 */
export function stepBuckets(
  run: OnboardingRun,
  step: OnboardingStepName,
  blocked: Set<string>,
): Record<'succeeded' | 'skipped' | 'failed' | 'delivery_unknown' | 'claimed' | 'pending' | 'blocked', number> {
  const counts = {
    succeeded: 0,
    skipped: 0,
    failed: 0,
    delivery_unknown: 0,
    claimed: 0,
    pending: 0,
    blocked: 0,
  }
  for (const target of run.targets) {
    const status = stepStatus(target, step)
    if (status === 'pending' && blocked.has(target.target_id)) counts.blocked += 1
    else if (status === 'running' || status === 'claimed') counts.claimed += 1
    else counts[status] += 1
  }
  return counts
}

export interface CreditsConfirmationRow {
  target_id: string
  email: string
  credits: number
  currentBalance: number | null
}

/** Built from SERVER state at click time (B5): the ready rows, each amount, the sum. */
export function creditsConfirmation(
  ready: OnboardingTarget[],
  preflight: OnboardingPreflight | undefined,
): { rows: CreditsConfirmationRow[]; total: number; count: number } {
  const balances = new Map(
    (preflight?.targets ?? []).map((t) => [t.target_id, t.current_balance]),
  )
  const rows = ready
    .filter((target) => target.credits > 0)
    .map((target) => ({
      target_id: target.target_id,
      email: target.email ?? '',
      credits: target.credits,
      currentBalance: balances.get(target.target_id) ?? null,
    }))
  return {
    rows,
    total: rows.reduce((sum, row) => sum + row.credits, 0),
    count: rows.length,
  }
}

export interface CreditWalletGroup {
  key: string
  kind: 'personal' | 'team'
  name: string
  credits: number
  currentBalance: number | null
  targetIds: string[]
}

/** Group display by the frozen destination, never by a team name or input order. */
export function creditWalletGroups(ready: OnboardingTarget[], preflight: OnboardingPreflight | undefined): CreditWalletGroup[] {
  const groups = new Map<string, CreditWalletGroup>()
  for (const row of creditsConfirmation(ready, preflight).rows) {
    const target = ready.find((candidate) => candidate.target_id === row.target_id)
    if (!target) throw new Error('Credit target is missing')
    if (target.wallet_kind === null) throw new Error('The credit wallet is not pinned')
    const team = target.wallet_kind === 'team'
    if (team && !target.credited_team_id) throw new Error('The team credit wallet is not pinned')
    const key = team ? `team:${target.credited_team_id}` : `personal:${target.target_id}`
    const group = groups.get(key)
    if (group) {
      group.credits += row.credits
      group.targetIds.push(row.target_id)
    } else groups.set(key, {
      key, kind: team ? 'team' : 'personal', name: team ? target.credit_wallet_name || target.credited_team_id! : row.email,
      credits: row.credits,
      currentBalance: target.credit_wallet_balance !== undefined ? target.credit_wallet_balance : team ? null : row.currentBalance,
      targetIds: [row.target_id],
    })
  }
  return [...groups.values()]
}

// ---------------------------------------------------------------------------
// Sequential batch runner (B3/B4)
// ---------------------------------------------------------------------------

/** Per-row refusals the server explains; the row is skipped, the batch goes on. */
const ROW_REFUSAL_CODES = new Set([
  'RECIPIENT_IN_FLIGHT',
  'RECIPIENT_ALREADY_ONBOARDED',
  'RUN_CREDIT_CEILING',
  'TARGET_PURGED',
])

export interface BatchError {
  status: number | null
  code: string | null
  message: string
}

export type RowOutcome =
  | { kind: 'done' }
  | { kind: 'failed'; message: string }
  | { kind: 'done_by_others' }
  | { kind: 'in_progress' }
  | { kind: 'skipped'; message: string }

export type BatchStop =
  | { kind: 'finished' }
  | { kind: 'stopped' }
  | { kind: 'cancelled' }
  | { kind: 'halted'; targetId: string; error: BatchError }

export interface BatchResult {
  outcomes: Record<string, RowOutcome>
  stop: BatchStop
}

export interface BatchDeps {
  /** Advance one step for one target. Rejects with a BatchError-like failure. */
  advance: (targetId: string) => Promise<void>
  /** Re-read the whole run from the server. */
  refetch: () => Promise<OnboardingRun>
  isStopped: () => boolean
  toBatchError: (error: Error) => BatchError
  onRowStart?: (targetId: string) => void
  onRowDone?: (targetId: string, outcome: RowOutcome) => void
  teamNotReadyMessage?: string
}

/**
 * Dispatch one step for each requested target, ONE request at a time, in row
 * order. Refetches the run at the start (so the ready set is the server's, not
 * a stale cache), on every conflict, and at the end.
 *
 * On a 409 the run is refetched and the row classified from authoritative
 * state. A 400 is a recorded per-row failure; the batch continues. Anything
 * unexplained — a 409 that does not classify, DELIVERY_UNKNOWN (the mail
 * transport is in doubt, so the next send would be too), a 404, a 5xx or a
 * network error — halts the batch and is surfaced.
 */
export async function runBatch(
  step: OnboardingStepName,
  requested: string[],
  blocked: Set<string>,
  deps: BatchDeps,
): Promise<BatchResult> {
  const outcomes: Record<string, RowOutcome> = {}
  let run = await deps.refetch()
  // Row order from the server's ready set, intersected with what the operator
  // confirmed — never the order the ids were passed in.
  const wanted = new Set(requested)
  const queue = batchTargets(run, step, blocked)
    .map((t) => t.target_id)
    .filter((id) => wanted.has(id))

  let stop: BatchStop = { kind: 'finished' }
  for (const targetId of queue) {
    if (deps.isStopped()) {
      stop = { kind: 'stopped' }
      break
    }
    if (step === 'team') {
      run = await deps.refetch()
      if (deps.isStopped()) { stop = { kind: 'stopped' }; break }
      if (run.status === 'cancelled') { stop = { kind: 'cancelled' }; break }
      const target = run.targets.find((candidate) => candidate.target_id === targetId)
      if (!readyTargets(run, step, blocked).some((candidate) => candidate.target_id === targetId)) {
        const status = target ? stepStatus(target, step) : null
        const outcome: RowOutcome = status === 'succeeded' || status === 'skipped' ? { kind: 'done_by_others' }
          : status === 'claimed' || status === 'running' ? { kind: 'in_progress' }
          : { kind: 'skipped', message: deps.teamNotReadyMessage ?? 'TEAM_SETUP_NOT_READY' }
        outcomes[targetId] = outcome
        deps.onRowDone?.(targetId, outcome)
        continue
      }
    }
    deps.onRowStart?.(targetId)
    let outcome: RowOutcome
    try {
      await deps.advance(targetId)
      outcome = { kind: 'done' }
    } catch (raw) {
      const error =
        raw instanceof Error
          ? deps.toBatchError(raw)
          : { status: null, code: null, message: String(raw) }
      if (error.status === 400) {
        outcome = { kind: 'failed', message: error.message }
      } else if (error.status === 409 && error.code !== 'DELIVERY_UNKNOWN') {
        run = await deps.refetch()
        const classified = classifyConflict(run, targetId, step, error)
        if (classified === 'cancelled') {
          stop = { kind: 'cancelled' }
          deps.onRowDone?.(targetId, { kind: 'skipped', message: error.message })
          outcomes[targetId] = { kind: 'skipped', message: error.message }
          break
        }
        if (classified === 'unexplained') {
          stop = { kind: 'halted', targetId, error }
          break
        }
        outcome = classified
      } else {
        stop = { kind: 'halted', targetId, error }
        break
      }
    }
    outcomes[targetId] = outcome
    deps.onRowDone?.(targetId, outcome)
  }

  await deps.refetch()
  return { outcomes, stop }
}

/** Classify a 409 from the freshly refetched run (B4). */
export function classifyConflict(
  run: OnboardingRun,
  targetId: string,
  step: OnboardingStepName,
  error: BatchError,
): RowOutcome | 'cancelled' | 'unexplained' {
  if (run.status === 'cancelled') return 'cancelled'
  const target = run.targets.find((t) => t.target_id === targetId)
  if (!target) return 'unexplained'
  const status = stepStatus(target, step)
  if (status === 'succeeded' || status === 'skipped') return { kind: 'done_by_others' }
  if (status === 'claimed' || status === 'running') return { kind: 'in_progress' }
  if (error.code !== null && ROW_REFUSAL_CODES.has(error.code)) {
    return { kind: 'skipped', message: error.message }
  }
  if (status === 'pending' && !predecessorDone(target, step)) {
    return { kind: 'skipped', message: error.message }
  }
  return 'unexplained'
}

/**
 * True for the 404 every switch-gated onboarding route answers when the tool
 * is turned off in this environment (ONBOARDING_ENABLED, P3 §5.4).
 */
export function isOnboardingDisabledError(error: Error | null): boolean {
  if (!(error instanceof ApiRequestError) || error.status !== 404) return false
  return isApiRecord(error.errorData) && isApiRecord(error.errorData.detail)
    ? error.errorData.detail.code === 'ONBOARDING_DISABLED'
    : false
}
